import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { html, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import type { Row } from '../../../../core/view-manager/row.js';
import type { CollapseState } from '../../core/collapse-state.js';
import type { GanttZoomLevel } from '../../define.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import { ROW_HEIGHT } from '../bars/bar-renderer.js';
import { swimlaneBodyStyle, swimlaneGroupStyle } from './swimlanes-css.js';

/**
 * Wraps the header + bar layer for a single group. The actual bar
 * rendering happens inside `dv-gantt-bar-renderer`, but this wrapper
 * drives the collapse state and the group-scoped row list so the bar
 * renderer stays unaware of grouping.
 */
export class GanttSwimlaneGroup extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor view!: GanttSingleView;

  @property({ attribute: false })
  accessor groupKey!: string;

  @property({ attribute: false })
  accessor groupLabel!: string;

  @property({ attribute: false })
  accessor rows!: readonly Row[];

  @property({ attribute: false })
  accessor collapseState!: CollapseState;

  @property({ attribute: false })
  accessor zoom: GanttZoomLevel = 'week';

  @property({ attribute: false })
  accessor originMs!: number;

  @property({ attribute: false })
  accessor fromMs!: number;

  @property({ attribute: false })
  accessor onBarClick: ((rowId: string) => void) | undefined;

  override connectedCallback(): void {
    super.connectedCallback();
    this.classList.add(swimlaneGroupStyle);
  }

  override render(): TemplateResult {
    const collapsed = this.collapseState.isCollapsed(this.groupKey);
    const rowsHeight = collapsed
      ? 0
      : Math.max(this.rows.length * ROW_HEIGHT, 0);

    return html`
      <dv-gantt-swimlane-header
        .label=${this.groupLabel}
        .rowCount=${this.rows.length}
        .collapsed=${collapsed}
        .onToggle=${() => this.collapseState.toggle(this.groupKey)}
      ></dv-gantt-swimlane-header>
      ${collapsed
        ? nothing
        : html`
            <div
              class=${swimlaneBodyStyle}
              style="height: ${rowsHeight}px;"
            ></div>
          `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-swimlane-group': GanttSwimlaneGroup;
  }
}
